import React from 'react'
import PropTypes from 'prop-types'
import uid from 'lodash/uniqueId'
import InputGroup from '../InputGroup'
import InputGroupAddon from '../InputGroupAddon'
import { Rows } from '../../layout'

/**
 * Groups a label and children together.
 */
const WithLabel = ({
  id,
  className,
  icon,
  label,
  topLabel,
  rightLabel,
  color,
  error,
  style,
  children,
  disabled,
  stretch,
  ...domProps
}) => {
  id = id || uid('input')
  color = error ? 'danger' : color

  const inputGroup = (
    <InputGroup className={className} style={style}>
      {(label || icon) && (
        <InputGroupAddon as="label" htmlFor={id} icon={icon} color={color} disabled={disabled}>
          {label}
        </InputGroupAddon>
      )}
      {children({ id, color, error, disabled, ...domProps })}
      {rightLabel && (
        <InputGroupAddon color={color} disabled={disabled}>
          {rightLabel}
        </InputGroupAddon>
      )}
    </InputGroup>
  )

  return !topLabel ? (
    inputGroup
  ) : (
    <Rows layout={`auto ${stretch ? 1 : 'auto'}`} gap=".25rem">
      <label htmlFor={id}>
        <b>{topLabel}</b>
      </label>
      {inputGroup}
    </Rows>
  )
}

WithLabel.defaultProps = {
  color: 'light',
}

WithLabel.propTypes = {
  /**
   * The label htmlFor.
   */
  id: PropTypes.string,
  /**
   * Classname applied to the root node.
   */
  className: PropTypes.string,
  /**
   * The icon to display.
   */
  icon: PropTypes.elementType,
  /**
   * An optional label to display alongside the icon.
   */
  label: PropTypes.node,
  /**
   * An optional label to display to the right.
   */
  rightLabel: PropTypes.node,
  /**
   * An optional label to display to the top.
   */
  topLabel: PropTypes.node,
  /**
   * The color of the icon holder.
   */
  color: PropTypes.string,
  /**
   * The error to display.
   */
  error: PropTypes.node,
  /**
   * Style applied to the root node.
   */
  style: PropTypes.object,
  /**
   * Whether or not the input is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The input to apply the label to as a render prop.
   */
  children: PropTypes.func.isRequired,
  /**
   * Whether or not the element should stretch vertically
   */
  stretch: PropTypes.bool,
}

export default WithLabel
